import { generateId } from './ids';
import type { PageType } from './pageMeta';

declare global {
  interface Window {
    dataLayer?: unknown[];
  }
}

export type ToolOutcome = 'success' | 'empty' | 'invalid' | 'error';
export type ResultKind = 'tool' | 'article' | 'country' | 'topic';

export interface GckEventParams {
  page_type?: PageType;
  tool_id?: string;
  tool_action?: string;
  tool_outcome?: ToolOutcome;
  tool_detail?: string;
  result_count?: number;
  query_length?: number;
  result_kind?: ResultKind;
  result_position?: number;
  article_slug?: string;
  article_topic?: string;
  read_depth?: number;
  placement?: string;
  cta_id?: string;
  source?: string;
  error_type?: string;
  destination?: string;
}

const PARAM_KEYS: Array<keyof GckEventParams> = [
  'page_type',
  'tool_id',
  'tool_action',
  'tool_outcome',
  'tool_detail',
  'result_count',
  'query_length',
  'result_kind',
  'result_position',
  'article_slug',
  'article_topic',
  'read_depth',
  'placement',
  'cta_id',
  'source',
  'error_type',
  'destination',
];

// GA4 truncates event parameter values at 100 characters.
const MAX_PARAM_LENGTH = 100;

// Deliberately in-memory only -- never a cookie, never localStorage, never
// sessionStorage. This is what lets the site run GA4 with no consent banner
// (docs/analytics-plan.md section 7). Persisting it would make the privacy
// policy's "stores nothing on your device" claim false. Do not "fix" it.
const clientId = generateId();

function push(payload: Record<string, unknown>): void {
  if (typeof window === 'undefined' || !Array.isArray(window.dataLayer)) return;
  window.dataLayer.push(payload);
}

function sanitize(value: unknown): string | number | boolean | undefined {
  if (value === undefined || value === null) return undefined;
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
  if (typeof value === 'boolean') return value;
  const trimmed = String(value).trim();
  if (!trimmed) return undefined;
  return trimmed.slice(0, MAX_PARAM_LENGTH);
}

export function initAnalytics(): void {
  push({ event: 'gck_init', client_id: clientId });
}

export function trackPageView(
  path: string,
  title: string,
  pageType: PageType,
  toolId?: string
): void {
  push({
    event: 'gck_page_view',
    page_path: sanitize(path),
    page_title: sanitize(title),
    page_type: pageType,
    tool_id: sanitize(toolId),
  });
  if (import.meta.env.DEV) {
    console.debug('[analytics] page_view', { path, title, pageType, toolId });
  }
}

export function trackToolUse(
  toolId: string,
  action: string,
  outcome: ToolOutcome,
  detail?: string,
  extra?: GckEventParams
): void {
  trackEvent('tool_use', {
    tool_id: toolId,
    tool_action: action,
    tool_outcome: outcome,
    tool_detail: detail,
    ...extra,
  });
}

export function trackEvent(name: string, params: GckEventParams = {}): void {
  const payload: Record<string, unknown> = { event: 'gck_event', ga_event: name };
  // Every key is emitted on every push, `undefined` included: GTM's data layer
  // merges pushes rather than replacing them, so a key omitted here would keep
  // the previous event's value and silently mislabel this one.
  PARAM_KEYS.forEach((key) => {
    payload[key] = sanitize(params[key]);
  });
  push(payload);
  if (import.meta.env.DEV) {
    console.debug('[analytics]', name, params);
  }
}
